import { useParams, Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { DetailError } from "@/components/DetailError";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTAButton } from "@/components/CTAButton";
import { Linkify } from "@/components/Linkify";
import { ArrowRight } from "lucide-react";

export default function GuidePage() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/content/guides/${slug}`, [slug]);

  if (loading) return <Loading />;
  if (error || !data) return <DetailError error={error} path={`/guides/${slug}`} notFound={!error} />;
  const g = data;

  const jsonLd = { "@context": "https://schema.org", "@type": "Article", headline: g.title, image: g.hero_image, articleSection: g.category };

  return (
    <>
      <Seo title={g.seo_title || `${g.title} | Hotelstrips`} description={g.seo_description || g.body?.slice(0, 155)}
        image={g.hero_image} path={`/guides/${g.slug}`} type="article" jsonLd={jsonLd} />

      <section className="relative min-h-[50vh] flex items-end">
        <img src={g.hero_image} alt={g.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 img-overlay" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8 w-full pb-12">
          <div className="overline text-primary mb-3">{g.category}</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white leading-tight">{g.title}</h1>
          <div className="mt-6" data-testid="guide-hero-cta">
            <CTAButton
              productType="hotels"
              size="lg"
              testId="guide-hero-hotels-cta"
              ctx={{ event: g.related_event_slug, eventDate: g.related_event_date, venue: g.related_venue_slug, city: g.related_city_slug, page: `guide_${g.slug}`, position: "hero" }}
            />
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <Breadcrumbs items={[{ label: "Guides" }, { label: g.title }]} />
        <div className="prose prose-lg max-w-none">
          {g.body?.split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="text-lg text-foreground/80 leading-relaxed mb-6"><Linkify text={p} /></p>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <CTAButton productType="hotels" ctx={{ event: g.related_event_slug, eventDate: g.related_event_date, venue: g.related_venue_slug, city: g.related_city_slug, page: `guide_${g.slug}`, position: "guide_body" }} />
          {g.related_venue_slug && (
            <Link to={`/venues/${g.related_venue_slug}`} className="inline-flex items-center gap-2 rounded-full border border-black/15 px-6 py-3 text-sm font-heading font-semibold text-ink hover:border-primary hover:text-primary transition-colors">
              View venue guide <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </article>
    </>
  );
}
